import React, {useCallback, useEffect, useRef, useState} from 'react';
import NomenclatureTemplate from './NomenclatureTemplate';
import {useNavigate} from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Checkbox,
    useTheme,
    useMediaQuery,
    Typography,
    Menu,
    MenuItem,
    Divider,
    ListItemIcon,
    ListItemText,
    Autocomplete,
    ListItem,
    CircularProgress,
} from '@mui/material';
import {
    DataGrid,
    GridApi,
    GridCellEditCommitParams,
    GridEvents,
    GridCellParams,
    GridCellValue,
    GridColDef,
    GridRowsProp,
    GridSortModel,
    GridToolbarContainer,
    GridValueGetterParams,
} from '@material-ui/data-grid';
import Tooltip from '@mui/material/Tooltip';

import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import NomenclaturePagePopup from './NomenclaturePagePopup/NomenclaturePagePopup';
import {
    IGetMarkingTypes,
    IGetMeasureUnits,
    IGetNomenclatures,
    IImportNomenclature,
    IParamsMarkingType,
    IParamsMeasureUnit,
    IParamsNomenclature,
    INomenclatureGroup,
    IGetNomenclatureGroups,
} from '../../interfaces/fetch/Nomenclature';
import instance from '../../instance';
import $config from '../../config';
import CallNotification from '../../context/NotificationContext';
import ApiRequest, {ISaveBase64CSV, NavCustomPagination, SaveBase64CSVToFile} from '../StdFunction';
import './nomenclature.css';
import {ISort} from '../../interfaces/fetch/common';
import PlusIcon from '../Icons/PlusIcon';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import {DeleteSweepOutlined, MoreHoriz} from '@material-ui/icons';
import {ConfirmDialog} from '../../ui/ConfirmDialog';
import {NomenclatureOptionsPopup} from './NomenclatureOptionsPopup';
import {NomenclatureGroupsManagerPopup} from './NomenclatureGroupsManagerPopup';
import {NomenclatureGroupEditPaper} from '../../ui/NomenclatureGroupEditPaper';
import {NomenclatureMoveToGroupPopup} from './NomenclatureMoveToGroupPopup';

let curPageNomenclature = 0;

// Границы ширины колонки «Наименование» при ручном изменении.
const NAME_COL_MIN_WIDTH = 100;
const NAME_COL_MAX_WIDTH = 1200;
const NAME_COL_WIDTH_STORAGE_KEY = 'nomenclatureNameColWidth';
const HIDDEN_COLUMNS_STORAGE_KEY = 'nomenclatureHiddenColumns';

// Столбцы, которые нельзя скрывать через фильтр столбцов.
const ALWAYS_VISIBLE_COLUMNS = ['__check__', ' ', 'name', 'barcode'];

const NomenclaturePage = (props: any) => {
    const {setCurrentPageClass, userId, isAdmin} = props;
    const navigate = useNavigate();
    const customPagination = NavCustomPagination;
    const {handleErrorNotification} = CallNotification();

    const [addPositionOpen, setAddPosition] = useState<boolean>(false);
    const [isNew, setNew] = useState<boolean>(true);

    const [markingTypes, setMarkingTypes] = useState<IParamsMarkingType[]>([]);
    const [measureUnits, setMeasureUnits] = useState<IParamsMeasureUnit[]>([]);
    const [tableValue, setTableValue] = useState<IParamsNomenclature[]>([]);
    const [activeRow, setActiveRow] = useState<any | null>(null);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [totalRows, setTotalRows] = useState<number>();
    const [pageSize] = useState<number>(100);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [page, setPage] = useState<number>(0);
    const [searchText, setSearchText] = useState<string>('');
    const [noDataText, setNoDataText] = useState<string>('Пока нет номенклатуры');

    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

    const [nomenclatureGroups, setNomenclatureGroups] = useState<INomenclatureGroup[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

    // Номер последнего запроса списка: применяем только самый свежий ответ,
    // а после локальной правки строки отбрасываем ответы, ушедшие в полёт до неё.
    const fetchSeqRef = useRef(0);

    // Актуальные строки таблицы вне замыканий рендера (для applyTableRows).
    const tableValueRef = useRef<IParamsNomenclature[]>([]);
    // Внутренний API грида, перехваченный из onStateChange.
    const gridApiRef = useRef<any>(null);

    /**
     * Единая точка обновления строк таблицы. Вьюпорт грида (v4) перерисовывается
     * только по внутренним событиям и не замечает смену пропа rows: строки с
     * исчезнувшими id остаются в DOM и падают с «No row with id … found».
     * Поэтому строки обновляются родным api.setRows, а событие rowsSet публикуется
     * сразу же (не дожидаясь троттлинга 100 мс внутри setRows) вьюпорт
     * перестраивается в том же тике, без окна с «призрачными» строками.
     */
    const applyTableRows = (next: IParamsNomenclature[]) => {
        tableValueRef.current = next;
        setTableValue(next);
        try {
            const api = gridApiRef.current;
            if (api) {
                api.setRows(next);
                api.publishEvent(GridEvents.rowsSet);
            }
        } catch (err) {
            if ($config.debugMode & $config.D_ERR) {
                console.log('applyTableRows: Ошибка обновления грида: ' + err.message);
            }
        }
    };

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [exportAnchor, setExportAnchor] = useState<null | {top: number; left: number}>(null);
    const [replaceSemicolon, setReplaceSemicolon] = useState<boolean>(false);
    const importInputRef = useRef<HTMLInputElement>(null);
    const isMenuOpen = Boolean(menuAnchor);
    const [confirmState, setConfirmState] = useState<{
        open: boolean;
        title?: string;
        content: React.ReactNode;
        confirmText?: string;
        confirmColor?: string;
        onConfirm: () => void | Promise<void>;
    }>({
        open: false,
        content: null,
        confirmText: 'Подтвердить',
        onConfirm: () => {},
    });

    const [nomenclatureOptionsOpen, setNomenclatureOptionsOpen] = useState(false);
    const [nomenclatureMoveToGroupOpen, setNomenclatureMoveToGroupOpen] = useState(false);
    const [nomenclatureGroupsManagerOpen, setNomenclatureGroupsManagerOpen] = useState(false);

    // Ширина колонки «Наименование»; null — автоширина (flex).
    const [nameColWidth, setNameColWidth] = useState<number | null>(() => {
        const saved = localStorage.getItem(NAME_COL_WIDTH_STORAGE_KEY);
        return saved ? Number(saved) : null;
    });

    // Скрытые столбцы таблицы: field -> true (скрыт).
    const [hiddenColumns, setHiddenColumns] = useState<Record<string, boolean>>(() => {
        try {
            return JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY) ?? '{}');
        } catch {
            return {};
        }
    });
    const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | {top: number; left: number}>(null);

    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isLg = useMediaQuery(theme.breakpoints.down('lg'));

    // Очищаем sessionStorage с выбранными позициями при возврате на страницу
    useEffect(() => {
        sessionStorage.removeItem('nomenclatureSelectedRows');
    }, []);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
    };

    // Начало перетаскивания правого края заголовка «Наименование».
    const handleNameColResizeStart = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const headerCell = (event.target as HTMLElement).closest('[role="columnheader"]') as HTMLElement | null;
        const startWidth = nameColWidth ?? headerCell?.offsetWidth ?? 200;
        const startX = event.clientX;
        let frame = 0;

        const getWidth = (e: MouseEvent) => Math.min(NAME_COL_MAX_WIDTH, Math.max(NAME_COL_MIN_WIDTH, startWidth + e.clientX - startX));

        const onMouseMove = (e: MouseEvent) => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => setNameColWidth(getWidth(e)));
        };
        const onMouseUp = (e: MouseEvent) => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            cancelAnimationFrame(frame);
            const width = getWidth(e);
            setNameColWidth(width);
            localStorage.setItem(NAME_COL_WIDTH_STORAGE_KEY, String(width));
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    // Двойной клик по ручке — возврат к автоширине.
    const handleNameColResizeReset = () => {
        setNameColWidth(null);
        localStorage.removeItem(NAME_COL_WIDTH_STORAGE_KEY);
    };

    // Показывает/скрывает столбец таблицы.
    const toggleColumnVisibility = (field: string) => {
        setHiddenColumns(prev => {
            const next = {...prev, [field]: !prev[field]};
            localStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    };

    const OnClickColumnsFilter = (e: React.MouseEvent<HTMLElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setColumnsMenuAnchor({top: rect.bottom, left: rect.left});
    };

    // Обработчик выбора/снятия всех строк
    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedRows(new Set());
            setIsAllSelected(false);
        } else {
            const allBarcodes = new Set(
                tableValue.map(row => row.barcode).filter((barcode): barcode is string => barcode !== undefined && barcode !== null),
            );
            setSelectedRows(allBarcodes);
            setIsAllSelected(true);
        }
    };

    // Обработчик выбора отдельной строки
    const handleSelectRow = (barcode: string) => {
        if (!barcode) return;

        setSelectedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(barcode)) {
                newSet.delete(barcode);
            } else {
                newSet.add(barcode);
            }
            return newSet;
        });
    };

    // Обновляем состояние "выбрано все" при изменении выбранных строк или данных
    useEffect(() => {
        if (tableValue.length > 0 && selectedRows.size === tableValue.length) {
            setIsAllSelected(true);
        } else {
            setIsAllSelected(false);
        }
    }, [selectedRows, tableValue]);

    // Функция удаления выбранных номенклатур
    const handleDeleteSelected = () => {
        if (selectedRows.size === 0 || !userId) return;

        setConfirmState({
            open: true,
            content: 'Вы уверены, что хотите удалить выбранные позиции?',
            confirmText: 'Удалить',
            confirmColor: theme.palette.error.dark,
            onConfirm: async () => {
                setConfirmState(prev => ({...prev, open: false}));
                try {
                    setLoading(true);
                    const response = await instance.post('/LK', {
                        method: 'DeleteNomenclatureList',
                        params: {
                            user_id: Number(userId),
                            barcode_list: Array.from(selectedRows),
                        },
                    });

                    handleErrorNotification(response, true);

                    if (!response.data.error) {
                        const deletedAll = selectedRows.size >= tableValue.length;
                        setSelectedRows(new Set());
                        setIsAllSelected(false);
                        // Остаёмся на текущей странице; если она опустела, на предыдущую.
                        const nextPage = deletedAll && curPageNomenclature > 0 ? curPageNomenclature - 1 : curPageNomenclature;
                        await fetchNomenclatures(nextPage, sortModel, searchText);
                    }
                } catch (error) {
                    console.error('Ошибка при удалении номенклатуры', error);
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    // Функция перемещения выбранных номенклатур
    const handleMoveNomenclatureListToGroupSelected = async (nomenclatureGroupId: number | null) => {
        if (selectedRows.size === 0 || !userId) return;

        if (!confirm) return;

        try {
            setLoading(true);
            const response = await instance.post('/LK', {
                method: 'MoveNomenclatureListToGroup',
                params: {
                    user_id: Number(userId),
                    barcode_list: Array.from(selectedRows),
                    nomenclature_group_id: nomenclatureGroupId ? Number(nomenclatureGroupId) : null,
                },
            });

            handleErrorNotification(response, true);

            if (!response.data.error) {
                // При активном фильтре по группе перемещённые строки выпадают из выборки.
                const removedFromView = selectedGroupId !== null && selectedRows.size >= tableValue.length;
                setSelectedRows(new Set());
                setIsAllSelected(false);
                const nextPage = removedFromView && curPageNomenclature > 0 ? curPageNomenclature - 1 : curPageNomenclature;
                await fetchNomenclatures(nextPage, sortModel, searchText);
            }
        } catch (error) {
            console.error('Ошибка при перемещении номенклатуры', error);
        } finally {
            setLoading(false);
        }
    };

    // Функция для тиражирования выбранных номенклатур
    const handleReplicationClick = () => {
        if (selectedRows.size === 0) return;

        const selectedItems = tableValue
            .filter(row => selectedRows.has(row.barcode || ''))
            .map(row => ({
                barcode: row.barcode,
                name: row.name,
                ntin: row.ntin,
            }));

        sessionStorage.setItem('nomenclatureSelectedRows', JSON.stringify(selectedItems));

        navigate('/nomenklatura/replication');
    };

    // Подтверждение очистки номенклатуры
    const handleRequestClearNomenclature = () => {
        handleCloseMenu();
        setConfirmState({
            open: true,
            confirmText: 'Очистить',
            confirmColor: theme.palette.error.dark,
            content: 'Вы точно уверены, что хотите очистить номенклатуру?',
            onConfirm: () => void handleClearNomenclature(),
        });
    };

    // Функция очистки номенклатуры
    const handleClearNomenclature = async () => {
        setConfirmState(prev => ({...prev, open: false}));
        if (!userId) return;

        try {
            setLoading(true);
            const response = await instance.post('/LK', {
                method: 'DeleteAllNomenclature',
                params: {user_id: Number(userId)},
            });
            handleErrorNotification(response, true);
            if (!response.data.error) {
                setSelectedRows(new Set());
                setIsAllSelected(false);
                await fetchNomenclatures();
            }
        } catch (error) {
            console.error('Ошибка при очистке номенклатуры', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchNomenclatureGroups = async () => {
        if (!userId) return;
        try {
            const request: IGetNomenclatureGroups = {
                method: 'GetNomenclatureGroups',
                params: {
                    user_id: Number(userId),
                    sort: [{column_name: 'ng.name', direction: 'ASC'}],
                },
            };
            const response = await instance.post('/LK', request);
            if (!response.data.error) {
                setNomenclatureGroups(response.data.nomenclature_groups || []);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleOpenGroupsManager = () => {
        setNomenclatureGroupsManagerOpen(true);
    };

    /**
     * Загрузка списка типов маркировки.
     */
    const fetchMarkingTypes = async () => {
        const request: IGetMarkingTypes = {
            method: 'GetMarkedType',
            params: {},
        };
        let response,
            data = [],
            k,
            row: any;
        try {
            response = await instance.post('/LK', request);
            handleErrorNotification(response);
            if (!response.data.error) {
                data[0] = {
                    marked_type_id: 0,
                    marked_type_name: ' ',
                };
                for (k in response.data.marked_type) {
                    // Индексация ИД-шником.
                    row = response.data.marked_type[k];
                    data[+row.marked_type_id] = row;
                }
                setMarkingTypes(data);
            } else {
                setMarkingTypes([]);
            }
        } catch (err) {
            if ($config.debugMode & $config.D_ERR) {
                console.log(request.method + ': Ошибка: ' + err.message);
            }
            setLoading(false);
        }
        return;
    };

    /**
     * Загрузка списка размерностей.
     */
    const fetchMeasureUnits = async () => {
        const request: IGetMeasureUnits = {
            method: 'GetMeasureUnit',
            params: {},
        };
        let response,
            data = [],
            k,
            row: any;
        try {
            response = await instance.post('/LK', request);
            handleErrorNotification(response);
            if (!response.data.error) {
                for (k in response.data.measure_unit) {
                    // Индексация ИД-шником.
                    row = response.data.measure_unit[k];
                    data[+row.measure_unit_id] = row;
                }
                setMeasureUnits(data);
            } else {
                setMeasureUnits([]);
            }
        } catch (err) {
            if ($config.debugMode & $config.D_ERR) {
                console.log(request.method + ': Ошибка: ' + err.message);
            }
            setLoading(false);
        }
        return;
    };

    /**
     * Возвращает параметры упорядочивания.
     * @param sort GridSortModel
     * @constructor
     */
    const GetSortParams = (sort?: GridSortModel) => {
        sort = sort == undefined ? sortModel : sort;
        if (!sort || sort.length <= 0) {
            return undefined;
        }
        let filter: ISort | undefined = {},
            s = sort[0];

        if (s.field == 'name') {
            // Упорядочить по "Наименование".
            filter.column_name = 'ntf.name';
        } else if (s.field == 'barcode') {
            // Упорядочить по "Штрихкоду".
            filter.column_name = 'ntf.barcode';
        } else if (s.field == 'ntin') {
            // Упорядочить по "NTIN".
            filter.column_name = 'ntf.ntin';
        }

        if (filter.column_name && s.sort) {
            filter.direction = s.sort == 'asc' ? 'ASC' : s.sort == 'desc' ? 'DESC' : undefined;
            return !filter.direction ? undefined : filter;
        }
        return undefined;
    };

    const onSearch = async () => {
        await onSubmitGetRequest(0, searchText);
    };

    /**
     * Получение/загрузка списка номенклатур.
     * silent – фоновое обновление: без индикатора загрузки.
     */
    const fetchNomenclatures = async (newPage: number = 0, sort?: GridSortModel, search?: string, opts?: {silent?: boolean}) => {
        if (!userId) {
            setLoading(false);
            return;
        }

        const fetchId = ++fetchSeqRef.current;
        if (!opts?.silent) {
            setLoading(true);
        }

        const request: IGetNomenclatures = {
            method: 'GetNomenclature',
            params: {
                user_id: Number(userId),
                limit: {
                    row_count: pageSize,
                    offset: pageSize * newPage,
                },
            },
        };

        if (search && search.trim()) {
            request.params.search = search.trim();
        }

        if (selectedGroupId !== null) {
            request.params.nomenclature_group_id = Number(selectedGroupId);
        }

        let s = await GetSortParams(sort);
        if (s) {
            request.params.sort = [s];
        }

        try {
            const response = await instance.post('/LK', request);
            // Пока ждали ответ, ушёл более свежий запрос или данные правили локально.
            if (fetchId !== fetchSeqRef.current) {
                return;
            }
            if (!response.data.error) {
                console.log('response.data.nomenclature', response.data.nomenclature);
                response.data.nomenclature = response.data.nomenclature.map((item: any) => ({
                    ...item,
                    is_social: false,
                }));
                applyTableRows(response.data.nomenclature);
                curPageNomenclature = newPage <= 0 ? 0 : newPage;
                setPage(curPageNomenclature);
            } else {
                // Ошибку с кодом 33 (Номенклатура не найдена) не отображаем
                if (parseInt(response.data?.error, 10) !== 33) {
                    handleErrorNotification(response);
                }
                if (parseInt(response.data?.error, 10) === 38) {
                    setNoDataText('Доступ к номенклатуре заблокирован. Пополните баланс.');
                } else {
                    setNoDataText('Пока нет номенклатуры');
                }
                applyTableRows([]);
            }
            setLoading(false);
            setTotalRows(+response.data.total);
        } catch (err) {
            if (fetchId !== fetchSeqRef.current) {
                return;
            }
            if ($config.debugMode & $config.D_ERR) {
                console.log(request.method + ': Ошибка: ' + err.message);
            }
            setLoading(false);
        }
    };

    const onSubmitGetRequest = useCallback(
        async (offset: number = 0, search?: string) => {
            if (!markingTypes || markingTypes.length == 0) {
                await fetchMarkingTypes();
            }
            if (!measureUnits || measureUnits.length == 0) {
                await fetchMeasureUnits();
            }
            if (!nomenclatureGroups || nomenclatureGroups.length == 0) {
                await fetchNomenclatureGroups();
            }
            await fetchNomenclatures(offset, sortModel, search);
        },
        [fetchNomenclatures, markingTypes, measureUnits, sortModel, nomenclatureGroups],
    );

    const rows: GridRowsProp = tableValue;

    /**
     * Обновление списка после сохранения карточки без перерисовки таблицы:
     * редактирование — правка строки на месте, создание — тихий перезапрос текущей страницы.
     */
    const handleNomenclatureSaved = (saved: IParamsNomenclature, wasNew: boolean) => {
        // При создании позиция строки в серверной выдаче неизвестна; при смене группы
        // строка могла выпасть из активного фильтра — в обоих случаях спрашиваем сервер.
        if (wasNew || (selectedGroupId !== null && saved.nomenclature_group_id !== selectedGroupId)) {
            void fetchNomenclatures(curPageNomenclature, sortModel, searchText, {silent: true});
            return;
        }
        const {user_id, ...fields} = saved;
        fetchSeqRef.current++;
        applyTableRows(tableValueRef.current.map(r => (r.barcode === saved.barcode ? {...r, ...fields} : r)));
    };

    /**
     * Обновление списка после удаления: строка убирается сразу,
     * затем страница тихо добирается с сервера (серверная пагинация).
     */
    const handleNomenclatureDeleted = (barcode: string) => {
        applyTableRows(tableValueRef.current.filter(r => r.barcode !== barcode));
        setTotalRows(prev => (prev ? prev - 1 : prev));
        // Удалили последнюю строку не первой страницы — уходим на предыдущую.
        const nextPage = tableValueRef.current.length < 1 && curPageNomenclature > 0 ? curPageNomenclature - 1 : curPageNomenclature;
        void fetchNomenclatures(nextPage, sortModel, searchText, {silent: true});
    };

    /**
     * Сохраняет цену, изменённую прямо в ячейке таблицы (без открытия карточки).
     */
    const onCellEditCommit = async (params: GridCellEditCommitParams) => {
        const field = params.field;
        if (field !== 'price' && field !== 'wholesale_price') return;

        const row = tableValueRef.current.find(r => r.barcode === params.id);
        if (!row) return;

        // Возврат прежнего значения в ячейку: новый массив заставляет грид перечитать rows.
        const revert = () => applyTableRows([...tableValueRef.current]);

        const oldValue = Number(row[field]) || 0;
        const newValue = Number(
            String(params.value ?? '')
                .trim()
                .replace(',', '.'),
        );

        if (newValue === oldValue) return;

        const request = {
            method: 'EditNomenclature',
            params: {
                user_id: Number(userId),
                name: row.name,
                barcode: row.barcode,
                ntin: row.ntin || '',
                vendor_code: row.vendor_code || '',
                price: field === 'price' ? newValue : Number(row.price) || 0,
                wholesale_price: field === 'wholesale_price' ? newValue : Number(row.wholesale_price) || 0,
                tax_percent: Number(row.tax_percent) || 0,
                free_price: Boolean(row.free_price),
                measure_unit_id: Number(row.measure_unit_id) || 0,
                nomenclature_group_id: row.nomenclature_group_id ? Number(row.nomenclature_group_id) : null,
                marking_type: row.marking_type,
                is_social: Boolean(row.is_social),
            },
        };

        try {
            const response = await instance.post('/LK', request);
            if (!response.data.error) {
                fetchSeqRef.current++;
                applyTableRows(tableValueRef.current.map(r => (r.barcode === row.barcode ? {...r, [field]: newValue} : r)));
                handleErrorNotification({data: {error: 0, message: response.data.message}}, true);
            } else {
                handleErrorNotification(response);
                revert();
            }
        } catch (err) {
            handleErrorNotification(err);
            revert();
        }
    };

    // Добавляем колонку с галочками
    const selectionColumn: GridColDef = {
        field: '__check__',
        width: 60,
        headerName: '',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
            <Checkbox
                checked={isAllSelected}
                indeterminate={selectedRows.size > 0 && !isAllSelected}
                onChange={handleSelectAll}
                color="primary"
            />
        ),
        renderCell: (params: GridCellParams) => {
            const barcode = params.row.barcode;
            if (!barcode) return null;

            return <Checkbox checked={selectedRows.has(barcode)} onChange={() => handleSelectRow(barcode)} color="primary" />;
        },
    };

    const columns: GridColDef[] = [
        selectionColumn,
        {
            field: ' ',
            width: isLg ? 50 : 75,
            headerName: ' ',
            cellClassName: 'cell-updated',
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridCellParams) => {
                const onClick = () => {
                    const api: GridApi = params.api;
                    const fields = api
                        .getAllColumns()
                        .map(c => c.field)
                        .filter(c => c !== '__check__' && !!c);
                    const thisRow: Record<string, GridCellValue> = {};

                    fields.forEach(f => {
                        thisRow[f] = params.getValue(params.id, f);
                    });
                    setNew(false);
                    setAddPosition(true);
                    setActiveRow(params.row);
                };

                return (
                    <Button variant="text" onClick={onClick} className={'button-update'}>
                        &nbsp;
                    </Button>
                );
            },
        },
        {
            field: 'name',
            headerName: 'Наименование',
            // Заданная вручную ширина имеет приоритет над автошириной.
            ...(nameColWidth !== null ? {width: nameColWidth} : {flex: isLg ? 1 : 0.7}),
            minWidth: isLg ? 200 : 100, // чтобы на телефоне не сжималось до одной буквы
            renderHeader: () => (
                <>
                    <div className="MuiDataGrid-columnHeaderTitle">Наименование</div>
                    <div
                        className="name-column-resizer"
                        title="Потяните, чтобы изменить ширину. Двойной клик – автоширина."
                        onMouseDown={handleNameColResizeStart}
                        onDoubleClick={handleNameColResizeReset}
                        onClick={e => e.stopPropagation()}
                    />
                </>
            ),
        },
        {
            field: 'barcode',
            headerName: 'Штрихкод',
            width: 150,
            cellClassName: 'cell-barcode',
        },
        {
            field: 'ntin',
            headerName: 'NTIN',
            width: 150,
            disableColumnMenu: true,
        },
        {
            field: 'vendor_code',
            sortable: false,
            disableColumnMenu: true,
            headerName: 'Артикул',
            width: 150,
        },
        {
            field: 'price',
            sortable: false,
            disableColumnMenu: true,
            headerName: 'Цена',
            width: 100,
            editable: true,
            cellClassName: 'cell-price',
        },
        {
            field: 'wholesale_price',
            sortable: false,
            disableColumnMenu: true,
            headerName: 'Цена опт.',
            width: 100,
            editable: true,
            cellClassName: 'cell-price',
        },
        {
            field: 'free_price',
            headerName: '*',
            description: 'Флаг: свободная цена.',
            sortable: false,
            disableColumnMenu: true,
            width: 40,
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.free_price ? '✔' : '';
            },
            renderCell: (params: GridCellParams) => {
                if (!params?.row.free_price) {
                    return <></>;
                }
                return (
                    <svg className="mark" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="7.5" cy="7.5" r="7.5" fill="#76D929" />
                        <path d="M5 7.4L6.71429 9L10 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                );
            },
        },
        {
            field: 'tax_percent',
            sortable: false,
            disableColumnMenu: true,
            headerName: 'Налог',
            width: 75,
        },
        // {
        //     field: 'is_social',
        //     sortable: true,
        //     disableColumnMenu: true,
        //     headerName: 'Социальный',
        //     width: 95,
        //     valueGetter: (params: GridValueGetterParams) => {
        //         return params.row.is_social ? 'Да' : '-';
        //     },
        // },
        {
            field: 'measure_unit_id',
            sortable: false,
            disableColumnMenu: true,
            headerName: 'Ед.изм.',
            width: isLg ? 100 : 140,
            valueGetter: (params: GridValueGetterParams) => {
                let k = +params.row.measure_unit_id;
                if (measureUnits[k]) {
                    return measureUnits[k].measure_unit_name;
                }
                return params.row.measure_unit_id;
            },
            renderCell: (params: GridCellParams) => {
                const text = params.value ? String(params.value) : '';

                return text ? (
                    <span title={text} style={{cursor: 'pointer'}}>
                        {text}
                    </span>
                ) : (
                    <span>{text}</span>
                );
            },
        },
        {
            field: 'marking_type',
            sortable: false,
            disableColumnMenu: true,
            headerName: 'Маркирован',
            width: isLg ? 130 : 150,
            valueGetter: (params: GridValueGetterParams) => {
                let k = +params.row.marking_type;
                if (markingTypes[k]) {
                    return markingTypes[k].marked_type_name;
                }
                return params.row.marking_type;
            },
            renderCell: (params: GridCellParams) => {
                const fullName = params.value ? String(params.value) : '';

                let shortName = fullName;

                // TODO Так-то костыль. Надо привязываться на marking_type
                if (fullName === 'Товар, маркированный кодами EAN8 или EAN13') {
                    shortName = 'EAN8/EAN13';
                } else if (fullName === 'Товар, подлежащий обязательной маркировке кодом Datamatrix') {
                    shortName = 'Datamatrix';
                } else if (fullName === 'Товар, подлежащий обязательной маркировке кодами EAN8, EAN13 или Datamatrix') {
                    shortName = 'EAN8/EAN13/Datamatrix';
                }

                return fullName ? (
                    <Tooltip title={fullName}>
                        <span style={{cursor: 'pointer'}}>{shortName}</span>
                    </Tooltip>
                ) : (
                    <></>
                );
            },
        },
        {
            field: 'nomenclature_group_id',
            headerName: 'Группа',
            sortable: false,
            disableColumnMenu: true,
            flex: isLg ? 0.8 : 0.7,
            minWidth: isLg ? 200 : 100, // чтобы на телефоне не сжималось до одной буквы
            valueGetter: (params: GridValueGetterParams) => {
                const groupId = params.row.nomenclature_group_id;
                if (!groupId) return '';
                const group = nomenclatureGroups.find(g => g.id === groupId);
                return group ? group.name : '';
            },
        },
    ];

    // Столбцы, которые пользователь может скрывать.
    const toggleableColumns = columns.filter(col => !ALWAYS_VISIBLE_COLUMNS.includes(col.field));

    const visibleColumns: GridColDef[] = columns.map(col => ({
        ...col,
        hide: hiddenColumns[col.field] && !ALWAYS_VISIBLE_COLUMNS.includes(col.field),
    }));

    /**
     * Добавляет номенклатуру.
     */
    const AddNomenclature = () => {
        setAddPosition(true);
        setNew(true);
        setActiveRow(null);
    };

    /**
     * Добавление номенклатур.<br>
     * Фактически, - импорт из данных CSV-файла.
     */
    const importNomenclature = async (data: string) => {
        if (!data) {
            return;
        }
        let request: IImportNomenclature = {
            method: 'ImportNomenclature',
            params: {
                user_id: Number(userId),
                data: data,
            },
        };

        setLoading(true);
        const response = await ApiRequest(request, handleErrorNotification);
        setLoading(false);
        if (!response?.data.error) {
            void fetchNomenclatures();
        }
    };

    /**
     * Обработка события импорта данных из файла.
     * @param e
     * @constructor
     */
    const onClickImport = (e: any) => {
        const input = e.target;
        const [file] = input.files;
        input.value = '';
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = function () {
            let data = btoa(String(reader.result));
            void importNomenclature(data);
        };
        reader.readAsBinaryString(file);
    };

    const onSortModelChange = (newSort: GridSortModel) => {
        if (JSON.stringify(sortModel) != JSON.stringify(newSort)) {
            setSortModel(newSort);
            // Без useCallback([]): иначе замыкание захватывает первый рендер,
            // где userId ещё пуст, а фильтры (группа, поиск) — начальные.
            void fetchNomenclatures(curPageNomenclature, newSort, searchText);
        }
        return false;
    };

    /**
     * Экспортирует номенклатуры.<br>
     * Фактически, - запись данных в CSV-файл.
     */
    const ExportNomenclature = async (encoding: 'utf-8' | 'windows-1251') => {
        if (!userId) {
            return;
        }
        let request: any = {
            method: 'ExportNomenclature',
            params: {
                user_id: Number(userId),
                encoding: encoding,
                replace_semicolon: replaceSemicolon,
            },
        };

        setLoading(true);
        const response = await ApiRequest(request, handleErrorNotification);
        setLoading(false);
        if (!response?.data.error) {
            const prop: ISaveBase64CSV = {
                base64: response?.data.nomenclature_export ?? '',
                filename: response?.data.file_name ?? 'Nomenclature',
                ext: Boolean(response?.data.file_name) ? '' : 'csv',
                encoding: encoding === 'windows-1251' ? 'latin1' : 'utf-8',
            };
            SaveBase64CSVToFile(prop);
        }
    };

    /**
     * Обработка события экспорта: открывает меню выбора кодировки.
     * @constructor
     */
    const OnClickExport = (e: React.MouseEvent<HTMLElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setExportAnchor({top: rect.bottom, left: rect.left});
        return false;
    };

    useEffect(() => {
        if (userId) {
            void onSubmitGetRequest();
        }
    }, [userId, selectedGroupId]);

    const customGridToolbar = () => (
        <GridToolbarContainer>
            <Button
                variant="outlined"
                sx={{
                    border: '1px solid #D1DAE3',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    height: '44px',
                    minWidth: '44px',
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #D1DAE3',
                    },
                }}
                fullWidth={isSm}
                onClick={OnClickColumnsFilter}
                title="Настройка видимости столбцов"
            >
                <ViewColumnOutlinedIcon />
            </Button>

            <Button
                variant="outlined"
                sx={{
                    border: '1px solid #D1DAE3',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    height: '44px',
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #D1DAE3',
                    },
                }}
                fullWidth={isSm}
                startIcon={<FileDownloadIcon />}
                onClick={() => {
                    importInputRef.current?.click();
                }}
            >
                <Typography variant="body2">ИМПОРТ</Typography>
            </Button>

            <Button
                variant="outlined"
                sx={{
                    border: '1px solid #D1DAE3',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    height: '44px',
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #D1DAE3',
                    },
                }}
                fullWidth={isSm}
                startIcon={<FileUploadIcon />}
                onClick={OnClickExport}
            >
                <Typography variant="body2">ЭКСПОРТ</Typography>
            </Button>
        </GridToolbarContainer>
    );

    return (
        <NomenclatureTemplate setCurrentPageClass={setCurrentPageClass} isAdmin={isAdmin} userId={userId}>
            <input ref={importInputRef} onChange={onClickImport} multiple={false} type="file" hidden />
            <Menu
                anchorReference="anchorPosition"
                anchorPosition={exportAnchor ?? undefined}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
            >
                <MenuItem
                    onClick={() => {
                        setExportAnchor(null);
                        void ExportNomenclature('windows-1251');
                    }}
                >
                    Windows-1251
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setExportAnchor(null);
                        void ExportNomenclature('utf-8');
                    }}
                >
                    UTF-8 (с казахским языком)
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => setReplaceSemicolon(prev => !prev)}>
                    <Checkbox checked={replaceSemicolon} size="small" sx={{p: 0, mr: 1}} />
                    Заменять «;» на «,» в наименовании
                </MenuItem>
            </Menu>
            <Menu
                anchorReference="anchorPosition"
                anchorPosition={columnsMenuAnchor ?? undefined}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
            >
                {toggleableColumns.map(col => (
                    <MenuItem key={col.field} onClick={() => toggleColumnVisibility(col.field)}>
                        <Checkbox checked={!hiddenColumns[col.field]} size="small" sx={{p: 0, mr: 1}} />
                        {col.headerName === '*' ? 'Свободная цена' : col.headerName}
                    </MenuItem>
                ))}
            </Menu>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isLg ? 'column' : 'row',
                    alignItems: isLg ? 'stretch' : 'center',
                    flexWrap: isLg ? 'nowrap' : 'wrap',
                    gap: 1.5,
                    marginBottom: 2,
                    zIndex: 10,
                    padding: isSm ? '0 8px 0 8px' : 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                        width: isSm ? '100%' : 'auto',
                    }}
                >
                    <Button
                        startIcon={<PlusIcon />}
                        disabled={!userId}
                        onClick={AddNomenclature}
                        sx={{
                            flex: isSm ? '1 1 auto' : '0 0 auto',
                        }}
                    >
                        Добавить позицию
                    </Button>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                            ml: isLg ? 0 : 2,
                        }}
                    >
                        {selectedRows.size > 0 && (
                            <>
                                <Tooltip title={`Переместить выбранные в группу (${selectedRows.size})`}>
                                    <IconButton
                                        onClick={() => void setNomenclatureMoveToGroupOpen(true)}
                                        sx={{
                                            border: '1px solid #ccc',
                                            borderRadius: '6px',
                                            backgroundColor: '#fff',
                                            height: '44px',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                        }}
                                        size="large"
                                    >
                                        <DriveFileMoveOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                                {isAdmin && (
                                    <Tooltip title={`Тиражировать выбранные (${selectedRows.size})`}>
                                        <IconButton
                                            onClick={handleReplicationClick}
                                            sx={{
                                                border: '1px solid #ccc',
                                                borderRadius: '6px',
                                                backgroundColor: '#fff',
                                                height: '44px',
                                                '&:hover': {
                                                    backgroundColor: '#f0f0f0',
                                                },
                                            }}
                                            size="large"
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title={`Удалить выбранные (${selectedRows.size})`}>
                                    <IconButton
                                        onClick={() => void handleDeleteSelected()}
                                        disabled={isLoading}
                                        sx={{
                                            border: '1px solid #ccc',
                                            borderRadius: '6px',
                                            backgroundColor: '#fff',
                                            height: '44px',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                        }}
                                        size="large"
                                    >
                                        <DeleteOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}

                        <IconButton
                            aria-label="more"
                            onClick={handleOpenMenu}
                            sx={{
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                backgroundColor: '#fff',
                                height: '44px',
                                '&:hover': {
                                    backgroundColor: '#f0f0f0',
                                },
                            }}
                            size="large"
                        >
                            <MoreHoriz />
                        </IconButton>

                        <Menu anchorEl={menuAnchor} open={isMenuOpen} onClose={handleCloseMenu}>
                            <MenuItem
                                onClick={() => {
                                    handleCloseMenu();
                                    setNomenclatureOptionsOpen(true);
                                }}
                                disabled={isLoading || !userId}
                            >
                                <ListItemIcon>
                                    <SettingsOutlinedIcon fontSize="medium" />
                                </ListItemIcon>
                                <ListItemText primary="Дополнительные опции номенклатуры" />
                            </MenuItem>
                            <MenuItem onClick={() => void handleRequestClearNomenclature()} disabled={isLoading || !userId}>
                                <ListItemIcon>
                                    <DeleteSweepOutlined fontSize="medium" />
                                </ListItemIcon>
                                <ListItemText primary="Очистить номенклатуру" />
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        marginLeft: isLg ? 0 : 'auto',
                        marginRight: isLg ? 0 : '315px', // ширина тулбара грида (кнопки столбцов/импорта/экспорта)
                    }}
                >
                    <Autocomplete
                        options={nomenclatureGroups}
                        PaperComponent={props => <NomenclatureGroupEditPaper {...props} onManageGroups={handleOpenGroupsManager} />}
                        getOptionLabel={option => option.name}
                        value={nomenclatureGroups.find(g => g.id === selectedGroupId) || null}
                        onChange={(event, newValue) => (newValue ? setSelectedGroupId(newValue.id) : setSelectedGroupId(null))}
                        renderOption={(props, option) => (
                            <ListItem {...props}>
                                <Typography sx={{width: '100%'}}>{option.name}</Typography>
                            </ListItem>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                placeholder="Группа номенклатуры"
                                sx={{
                                    minWidth: 250,
                                    backgroundColor: '#fff',
                                    '.MuiOutlinedInput-root': {
                                        height: '44px',
                                        paddingTop: '0',
                                        paddingBottom: '0',
                                    },
                                }}
                            />
                        )}
                        disableClearable={selectedGroupId === null}
                        sx={{width: isLg ? '100%' : 'auto'}}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'nowrap',
                            width: isLg ? '100%' : 'auto',
                        }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    void onSearch();
                                }
                            }}
                            placeholder="Поиск по номенклатуре"
                            disabled={isLoading}
                            InputProps={{
                                endAdornment: searchText && (
                                    <IconButton
                                        title="Очистить"
                                        aria-label="Очистить"
                                        size="small"
                                        onClick={() => {
                                            setSearchText('');
                                            void onSubmitGetRequest(0, '');
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                            sx={{
                                flex: 1,
                                width: isLg ? '100%' : 260,
                                backgroundColor: '#fff',
                                '.MuiOutlinedInput-root': {
                                    height: '44px',
                                },
                            }}
                        />
                        <IconButton
                            onClick={() => void onSearch()}
                            sx={{
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                backgroundColor: '#fff',
                                height: '44px',
                                '&:hover': {
                                    backgroundColor: '#f0f0f0',
                                },
                            }}
                            size="large"
                            disabled={isLoading}
                        >
                            <SearchIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <div className="work-space">
                {isLoading && (
                    <div className="table-loading-overlay">
                        <CircularProgress />
                    </div>
                )}
                <DataGrid
                    className="table stripe"
                    onStateChange={(params: any) => {
                        gridApiRef.current = params.api;
                    }}
                    autoHeight={true}
                    columns={visibleColumns}
                    rows={rows}
                    rowCount={totalRows}
                    getRowId={row => row.barcode}
                    page={page}
                    pageSize={pageSize}
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    onCellEditCommit={params => void onCellEditCommit(params)}
                    loading={isLoading}
                    disableSelectionOnClick
                    components={{
                        Toolbar: customGridToolbar,
                        Pagination: customPagination,
                        // Встроенный оверлей привязан к внутреннему контейнеру грида,
                        // который схлопывается при пустой таблице; вместо него рисуем
                        // свой .table-loading-overlay поверх всего .work-space.
                        LoadingOverlay: () => null,
                    }}
                    componentsProps={{
                        header: {className: 'grid-header', style: {width: '315px', float: 'right'}},
                        pagination: {className: 'grid-pagination'},
                    }}
                    disableColumnSelector={true}
                    disableColumnFilter={true}
                    scrollbarSize={5}
                    onPageChange={newPage => {
                        void onSubmitGetRequest(newPage, searchText);
                    }}
                    localeText={{
                        noRowsLabel: noDataText,
                        toolbarExport: 'Экспорт',
                    }}
                    style={{
                        minHeight: '400px',
                        marginTop: isLg ? 80 : 0,
                    }}
                />
            </div>
            <NomenclaturePagePopup
                userId={userId}
                addPositionOpen={addPositionOpen}
                setAddPosition={setAddPosition}
                isNew={isNew}
                activeRow={activeRow}
                onSaved={handleNomenclatureSaved}
                onDeleted={handleNomenclatureDeleted}
                markingTypes={markingTypes}
                measureUnits={measureUnits}
                nomenclatureGroups={nomenclatureGroups}
                refreshGroups={() => void fetchNomenclatureGroups()}
            />
            <ConfirmDialog
                open={confirmState.open}
                onClose={() => setConfirmState(prev => ({...prev, open: false}))}
                onConfirm={confirmState.onConfirm}
                confirmColor={confirmState.confirmColor}
                isLoading={isLoading}
                confirmText={confirmState.confirmText}
                content={confirmState.content}
            />
            <NomenclatureOptionsPopup open={nomenclatureOptionsOpen} userId={userId} onClose={() => setNomenclatureOptionsOpen(false)} />
            <NomenclatureGroupsManagerPopup
                open={nomenclatureGroupsManagerOpen}
                userId={userId}
                onClose={() => {
                    setNomenclatureGroupsManagerOpen(false);
                }}
                onChange={() => void fetchNomenclatureGroups()}
            />
            <NomenclatureMoveToGroupPopup
                open={nomenclatureMoveToGroupOpen}
                userId={userId}
                onClose={() => setNomenclatureMoveToGroupOpen(false)}
                selectedRows={selectedRows}
                handleMoveNomenclatureListToGroupSelected={handleMoveNomenclatureListToGroupSelected}
                nomenclatureGroups={nomenclatureGroups}
                onManageGroups={handleOpenGroupsManager}
            />
        </NomenclatureTemplate>
    );
};

export default NomenclaturePage;
